import { defineStore } from "pinia";
import { type Component, computed, ref } from "vue";
import { Future } from "../util/misc";

export type DialogFuture<TDialogResolve = void> = Future<
  TDialogResolve extends void ? boolean : [boolean, TDialogResolve]
>;

export interface AsyncDialogProps<TDialogReturn = void> {
  future?: DialogFuture<TDialogReturn>;
}

export interface Dialog {
  component: Component;
  props?: Record<string, unknown>;
  top?: boolean;
}

export interface AsyncDialog<ReturnType = void> extends Dialog {
  props: AsyncDialogProps<ReturnType> & Dialog["props"];
}

export const useDialogStore = defineStore("dialog", () => {
  const dialogs = ref<Dialog[]>([]);

  const currentDialog = computed(() => dialogs?.value[0]);
  function popDialog() {
    dialogs.value.splice(0, 1);
  }

  /**
   * Creates a dialog and returns a future that will be resolved when the dialog is closed.
   *
   * Use the type parameter to specify the type of value that will be returned when the dialog is closed
   * for Typing support.
   *
   * Example usage:
   * ```ts
   * const askForAName = () => {
   *   // create a dialog asking for a string value
   *   const [wasConfirmed, yourName] = await createDialog<string>({
   *                       // the dialog component returns ^^^^^^ after confirmation
   *    component: MyDialogComponent,
   *    props: { someProp: "value" },
   *   });
   * }
   *
   * // do something with the value of `yourName`
   * if (wasConfirmed) {
   *   console.log("Your name is: ", yourName);
   * }
   * ```
   *
   * @param dialog Dialog to be displayed
   * @returns A future that will be resolved when the dialog is closed
   */
  function createDialog<TDialogReturn = void>(dialog: Dialog) {
    const d = dialog as AsyncDialog<TDialogReturn>;

    if (d.props == null) {
      d.props = {};
    }

    d.props.future = new Future<TDialogReturn extends void ? boolean : [boolean, TDialogReturn]>();
    if (dialog.top) {
      dialogs.value.unshift(d);
    } else {
      dialogs.value.push(d);
    }

    return d.props.future;
  }
  return { createDialog, dialogs, popDialog, currentDialog };
});
